<template>
  <article data-component-name="ArticlePreviewDefault">
    <div class="time-info">
      <time :datetime="article.publish_at" pubdate>
        {{ $dayjs(article.publish_at).format('MMMM DD, YYYY') }}
      </time>

      <span v-if="article.data?.data?.preview_time_to_read">
        | {{ article.data.data.preview_time_to_read }} {{ $t('min_to_read') }}
      </span>
    </div>

    <h2 v-html="article.data.title" />

    <div v-once v-html="article.data.preview" class="markup" />

    <div class="row">
      <Buttons.Link :to="articleLink" target="_blank">
        {{ article.data?.data?.preview_button_text || $t('Read_the_Article') }}
        <SvgIcon name="arrow-forward" />
      </Buttons.Link>

      <NuxtLink
        v-if="firstAuthor"
        :to="authorLink"
        target="_blank"
        class="author"
      >
        <AppAvatar
          :src="firstAuthor.data?.data?.avatar"
          :alt="firstAuthor.data.name"
          :initials="getInitials(firstAuthor.data.name)"
        />

        <span v-if="firstAuthor">
          {{ firstAuthor?.data.name }}
        </span>
      </NuxtLink>
    </div>
  </article>
</template>

<script setup lang="ts">
// components
import AppAvatar from '~/components/redesign/AppAvatar.vue';
import Buttons from '~/components/redesign/Buttons';

// composables
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// utils
import { getAuthorLink } from '~/utils/getAuthorLink';
import { getArticleLink } from '~/utils/getArticleLink';
import getInitials from '~/utils/getInitials';

// types
import type { Author } from '~/types/author';
import type { BlogArticle } from '~/types/blog';

const props = defineProps<{ article: BlogArticle }>();
const firstAuthor = computed<Author | undefined>(
  () => props.article.authors && props.article.authors[0],
);

const localePathWithSlash = useLocalePathWithSlash();

const articleLink = computed(() =>
  localePathWithSlash(getArticleLink(props.article)),
);
const authorLink = computed(() =>
  localePathWithSlash(getAuthorLink(firstAuthor.value)),
);
</script>

<style scoped lang="scss">
@use '_/fn';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/text-transform';
@use '_/mixins/size';

[data-component-name='ArticlePreviewDefault'] {
  width: 100%;
  border: 1px solid fn.token('outline-secondary');
  padding: 2.5rem;

  @include media.tablet {
    padding: 1.5rem;
  }

  @include media.mobile {
    padding: 1.5rem 1rem;
  }

  .time-info {
    @include typo.body-3;

    margin-bottom: 1rem;

    time {
      text-transform: capitalize;
    }
  }

  h2 {
    @include typo.subtitle-1;

    margin-bottom: 0.5rem;
  }

  &:deep(.markup) {
    @include typo.body-3;

    margin-bottom: 2rem;

    @include text-transform.truncate-to-rows(6);

    @include media.mobile {
      margin-bottom: 1.5rem;
    }
  }

  .row {
    @include flex.center-sb;

    @include media.mobile {
      flex-direction: column;
    }

    a.author {
      @include flex.center-center;

      gap: 1rem;

      @include media.mobile {
        margin-top: 1rem;
      }

      img {
        @include size.fixed(3.5rem);

        border-radius: 100px;
        border: 1px solid fn.token('outline-secondary');
      }

      span {
        @include typo.subtitle-2;
      }
    }

    [data-component-name='Buttons.Link'] {
      display: block;
      width: max-content;

      @include media.mobile {
        width: 100%;
      }
    }
  }
}
</style>
